import { http, tryHttp } from '@pequity/shared/axios/http';
import { fetchCyclePermissionsInfo } from '@pequity/shared/ccp/query/ccp-details';
import { cycleInfoQueryOptions } from '@pequity/shared/ccp/query/ccp-settings';
import type { ConfigApiUserRole } from '@pequity/shared/ccp/types';
import type { Permission, PermissionsInfo } from '@pequity/shared/ccp/types/ccp-details';
import type { SettingsRole } from '@pequity/shared/ccp/types/ccp-settings';
import { useQueryClient } from '@tanstack/vue-query';
import { type NavigationGuard } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

export const companyUsesCcpV2: NavigationGuard = () => {
  const companyStore = useCompanyStore();
  const hasCcp = companyStore.hasCompCyclePlanning;

  if (hasCcp) {
    return true;
  } else {
    return { name: 'home' };
  }
};

export const redirectToHomeIfNotAdmin: NavigationGuard = async () => {
  const authStore = useAuthStore();

  if (authStore.isUserAdmin) {
    return true;
  } else {
    return { name: 'ccpv2-list' };
  }
};

export const redirectToHomeIfNoCcpAccess: NavigationGuard = async () => {
  const authStore = useAuthStore();

  if (authStore.appAccess.nav.comp_cycle) {
    return true;
  } else {
    return { name: 'home' };
  }
};

export const redirectToNotFoundIfNoWaveId: NavigationGuard = async (to) => {
  if (!to.params.waveId) {
    return { name: 'ccpv2-only-404' };
  }

  return true;
};

export const redirectToHomeIfNotPermission =
  (permission: Permission): NavigationGuard =>
  async (to) => {
    const { data, error } = await tryHttp(http.get<SettingsRole>(`v2/ccp/roles/${to.params.roleId}/permissions`));

    if (error || !data[permission]) {
      return { name: 'ccpv2-list' };
    }

    return true;
  };

export const loadCycleAndRedirectIfArchived: NavigationGuard = async (to) => {
  const queryClient = useQueryClient();
  const cycleId = Number(to.params.cycleId);
  const isAdminRoute = to.name === 'ccpv2-admin-details';
  const role: ConfigApiUserRole = isAdminRoute ? 'admin' : 'planner';
  const permissionsVars = {
    roleId: isAdminRoute ? ('admin' as const) : Number(to.params.roleId),
  };

  const [cycle] = await Promise.all([
    queryClient.fetchQuery(cycleInfoQueryOptions({ cycleId, role })),
    queryClient.fetchQuery({
      queryKey: ['cyclePermissionsInfo', permissionsVars],
      queryFn: () => fetchCyclePermissionsInfo(permissionsVars),
    }),
  ]);

  if (cycle.hidden && !isAdminRoute) {
    return { name: 'ccpv2-list' };
  }

  return true;
};

export const redirectIfInactiveGroup: NavigationGuard = async (to) => {
  const authStore = useAuthStore();

  if (authStore.isUserAdmin) {
    return true;
  }

  const queryClient = useQueryClient();

  const vars = { roleId: Number(to.params.roleId) };
  const permissionsInfo = queryClient.getQueryData<PermissionsInfo>(['cyclePermissionsInfo', vars]);

  if (!permissionsInfo?.is_active && !permissionsInfo?.allow_inactive_group_view) {
    return { name: 'ccpv2-list' };
  }

  return true;
};
